// Le sélecteur de portée : le titre de la barre du haut (« Tous les dossiers »
// ou le nom du dossier ouvert) déroule la liste des dossiers du cabinet, pour
// passer de l'un à l'autre sans revenir au portefeuille. « Tous les dossiers »
// reste en tête de liste et y ramène.
//
// La liste n'est montée, donc chargée, qu'à l'ouverture : le titre est sur
// tous les écrans, et la plupart du temps personne ne le déroule.

import "@app/components/compta/ui/menu.css";
import "./selecteur-dossier.css";

import { ComptaIcone } from "@app/components/compta/ui/ComptaIcone";
import { useDossiersDuCabinet } from "@app/hooks/compta/useDossiersDuCabinet";
import type { LightWorkspaceType } from "@app/types/user";
import { useEffect, useRef, useState } from "react";

// Au-delà, un champ filtre la liste par son nom.
const SEUIL_RECHERCHE = 7;

interface SelecteurDossierProps {
  owner: LightWorkspaceType;
  titre: string;
  // L'espace du dossier ouvert, absent sur la portée cabinet.
  dossierActif?: string;
  onChoisirDossier: (spaceId: string) => void;
  onTousLesDossiers: () => void;
}

export function SelecteurDossier({
  owner,
  titre,
  dossierActif,
  onChoisirDossier,
  onTousLesDossiers,
}: SelecteurDossierProps) {
  const [ouvert, setOuvert] = useState(false);
  const racine = useRef<HTMLDivElement>(null);

  // Un clic hors de la liste ou Échap la referment.
  useEffect(() => {
    if (!ouvert) {
      return;
    }
    const dehors = (evenement: MouseEvent) => {
      if (
        evenement.target instanceof Node &&
        !racine.current?.contains(evenement.target)
      ) {
        setOuvert(false);
      }
    };
    const clavier = (evenement: KeyboardEvent) => {
      if (evenement.key === "Escape") {
        setOuvert(false);
      }
    };
    document.addEventListener("mousedown", dehors);
    document.addEventListener("keydown", clavier);
    return () => {
      document.removeEventListener("mousedown", dehors);
      document.removeEventListener("keydown", clavier);
    };
  }, [ouvert]);

  return (
    <div className="cselecteur" ref={racine}>
      <button
        type="button"
        className="ccoquille__scope"
        aria-haspopup="menu"
        aria-expanded={ouvert}
        onClick={() => setOuvert((etat) => !etat)}
      >
        {titre}
        <ComptaIcone nom="caret-down" taille={13} />
      </button>
      {ouvert && (
        <ListeDesDossiers
          owner={owner}
          dossierActif={dossierActif}
          onChoisirDossier={(spaceId) => {
            setOuvert(false);
            onChoisirDossier(spaceId);
          }}
          onTousLesDossiers={() => {
            setOuvert(false);
            onTousLesDossiers();
          }}
        />
      )}
    </div>
  );
}

interface ListeDesDossiersProps {
  owner: LightWorkspaceType;
  dossierActif?: string;
  onChoisirDossier: (spaceId: string) => void;
  onTousLesDossiers: () => void;
}

function ListeDesDossiers({
  owner,
  dossierActif,
  onChoisirDossier,
  onTousLesDossiers,
}: ListeDesDossiersProps) {
  const [filtre, setFiltre] = useState("");
  const { dossiers, isDossiersLoading, isDossiersError } = useDossiersDuCabinet(
    { owner }
  );

  const recherche = filtre.trim().toLocaleLowerCase("fr");
  const visibles = recherche
    ? dossiers.filter((dossier) =>
        dossier.raisonSociale.toLocaleLowerCase("fr").includes(recherche)
      )
    : dossiers;

  return (
    <div className="cselecteur__liste">
      {dossiers.length >= SEUIL_RECHERCHE && (
        <input
          type="search"
          className="cselecteur__filtre"
          placeholder="Chercher un dossier"
          aria-label="Chercher un dossier"
          value={filtre}
          autoFocus
          onChange={(evenement) => setFiltre(evenement.target.value)}
        />
      )}
      <div className="cmenu cselecteur__menu" role="menu">
        <button
          type="button"
          role="menuitem"
          className="cmenu__element"
          onClick={onTousLesDossiers}
        >
          <ComptaIcone nom="squares-four" taille={16} />
          <span className="cmenu__libelle">Tous les dossiers</span>
          {!dossierActif && (
            <ComptaIcone nom="check" taille={14} className="cmenu__coche" />
          )}
        </button>
        <div className="cmenu__filet" />
        {isDossiersLoading ? (
          <div className="cselecteur__etat">Chargement des dossiers…</div>
        ) : isDossiersError ? (
          <div className="cselecteur__etat">
            Les dossiers n'ont pas pu être chargés.
          </div>
        ) : visibles.length === 0 ? (
          <div className="cselecteur__etat">
            {recherche ? "Aucun dossier à ce nom." : "Aucun dossier."}
          </div>
        ) : (
          visibles.map((dossier) => (
            <button
              key={dossier.id}
              type="button"
              role="menuitem"
              className="cmenu__element"
              onClick={() => onChoisirDossier(dossier.id)}
            >
              <span className="cmenu__libelle">{dossier.raisonSociale}</span>
              {dossier.id === dossierActif && (
                <ComptaIcone nom="check" taille={14} className="cmenu__coche" />
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
